import {
  APIFlaUserProfile,
  APIReportingUserProfile,
  UserProfile
} from '@partnerconnect-monorepo/types/user';
import { axios } from '../core/AxiosInterceptor/AxiosInterceptor';
import {
  getLocationBasedOnAdminBreakdown,
  mapToCamelCase
} from '../helpers/helpers';
import { getReportingUserRoleOptions } from '../utils/roles';
import { useQuery } from '@tanstack/react-query';

const getReportingUserProfile = () =>
  axios
    .get(`${import.meta.env.VITE_REPORTING_APP_BE_BASE}/profile/me`)
    .then<APIReportingUserProfile>((res) => mapToCamelCase(res.data))
    .catch(() => null);

const getFlaUserProfile = () =>
  axios
    .get(`${import.meta.env.VITE_FLA_APP_BE_BASE}/profile/me`)
    .then<APIFlaUserProfile>((res) => mapToCamelCase(res.data))
    .catch(() => null);

export const useReportingUserProfile = () =>
  useQuery({
    queryKey: ['REPORTING_USER_PROFILE'],
    queryFn: getReportingUserProfile
  });

export const useFlaUserProfile = () =>
  useQuery({
    queryKey: ['FLA_USER_PROFILE'],
    queryFn: getFlaUserProfile
  });

const mapReportingUserProfileToUserProfile = (
  reportingUserProfile: APIReportingUserProfile
): UserProfile => {
  const {
    // exclude country and partnerName
    country: reportingCountry,
    partnerName: reportingPartnerName,
    ...reporting
  } = reportingUserProfile;

  return {
    ...reporting,
    reportingRoles: reporting.roles,
    reportingCountry,
    reportingPartnerName,
    partnerships: reporting.partnerships.map(
      ({ activityTags: apiActivityTags, ...otherPartnershipProps }) => ({
        ...otherPartnershipProps,
        activityTags: apiActivityTags.map((activityTag) => ({
          ...activityTag,
          locations: activityTag.locations.map((location) =>
            getLocationBasedOnAdminBreakdown(location)
          )
        }))
      })
    ),
    canAssignWfpRolesList: getReportingUserRoleOptions(
      reporting.canAssignWfpRolesList
    ),
    canAssignPartnerRolesList: getReportingUserRoleOptions(
      reporting.canAssignPartnerRolesList
    ),
    allowedModules: {
      canAccessReporting: true,
      canAccessPartnerships: false
    }
  };
};

const mapFlaUserProfileToUserProfile = (
  flaUserProfile: APIFlaUserProfile
): UserProfile => {
  const {
    // exclude country and partnerName
    country: partnershipsCountry,
    partnerName: flaPartnerName,
    ...fla
  } = flaUserProfile;

  return {
    ...fla,
    flaRoles: fla.roles,
    partnershipsCountry,
    flaPartnerName,
    allowedModules: {
      canAccessReporting: false,
      canAccessPartnerships: true
    }
  };
};

const mergePartialUserProfiles = (
  reportingUserProfile: APIReportingUserProfile,
  flaUserProfile: APIFlaUserProfile
): UserProfile => ({
  ...mapReportingUserProfileToUserProfile(reportingUserProfile),
  ...mapFlaUserProfileToUserProfile(flaUserProfile),
  firstName: reportingUserProfile.firstName || flaUserProfile.firstName,
  lastName: reportingUserProfile.lastName || flaUserProfile.lastName,
  permissions: [
    ...reportingUserProfile.permissions,
    ...flaUserProfile.permissions
  ],
  roles: [...reportingUserProfile.roles, ...flaUserProfile.roles],
  allowedModules: {
    canAccessReporting: true,
    canAccessPartnerships: true
  }
});

export const getUserProfile = (
  reportingUserProfile: APIReportingUserProfile | null,
  flaUserProfile: APIFlaUserProfile | null
): UserProfile | null => {
  if (!!reportingUserProfile && !!flaUserProfile) {
    return mergePartialUserProfiles(reportingUserProfile, flaUserProfile);
  } else if (reportingUserProfile) {
    return mapReportingUserProfileToUserProfile(reportingUserProfile);
  } else if (flaUserProfile) {
    return mapFlaUserProfileToUserProfile(flaUserProfile);
  } else return null;
};
